<template>
  <div class="projects-page">
    <!-- Main Container -->
    <div class="projects-container">
      <!-- Page Header with Stats -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-text">
            <h1 class="page-title">Projects</h1>
            <p class="page-subtitle">
              Select a project to view and manage its associated pipelines
            </p>
          </div>
          <div
            v-if="!isLoading && !error && projects.length > 0"
            class="header-stats"
          >
            <div class="quick-stat">
              <span class="quick-stat-value">{{ projects.length }}</span>
              <span class="quick-stat-label">Total Projects</span>
            </div>
            <div class="quick-stat">
              <span class="quick-stat-value">{{
                filteredProjects.length
              }}</span>
              <span class="quick-stat-label">Showing</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Search and Filter Bar -->
      <div v-if="!isLoading && !error" class="controls-section">
        <div class="search-wrapper">
          <svg
            class="search-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search projects by name or ID..."
            class="search-input"
          />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="clear-search"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <button @click="refreshProjects" class="btn btn-secondary refresh-btn">
          <svg
            class="refresh-icon"
            :class="{ spinning: isRefreshing }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loader"></div>
        <span>Loading projects...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3>Error Loading Projects</h3>
        <p>{{ error }}</p>
        <button @click="refreshProjects" class="btn btn-primary">
          Try Again
        </button>
      </div>

      <!-- Projects Grid -->
      <div v-else-if="filteredProjects.length > 0" class="projects-grid">
        <router-link
          v-for="project in filteredProjects"
          :key="project.id"
          :to="`/associated-pipelines/${encodeURIComponent(project.name)}/${
            project.id
          }`"
          class="project-card"
        >
          <div class="project-card-header">
            <div class="project-icon">
              <span>{{ getProjectIcon(project.name) }}</span>
            </div>
            <div class="project-arrow">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
          <div class="project-content">
            <h2 class="project-title">{{ project.name }}</h2>
            <div class="project-meta">
              <span class="meta-item">
                <svg
                  class="meta-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
                {{ formatProjectId(project.id) }}
              </span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
        <h3>No Projects Found</h3>
        <p>
          {{
            searchQuery
              ? "Try adjusting your search criteria"
              : projects.length === 0
              ? "No projects available. Make sure you have access to Azure DevOps projects."
              : "No projects match your search"
          }}
        </p>
        <button
          v-if="searchQuery"
          @click="searchQuery = ''"
          class="btn btn-secondary"
        >
          Clear Search
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import { fetchProjects } from "@/azure-devops-api.js";
import { mapState } from "vuex";

export default {
  name: "ProjectsPage",
  components: {},
  data() {
    return {
      projects: [],
      searchQuery: "",
      isLoading: false,
      isRefreshing: false,
      error: null,
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),

    filteredProjects() {
      if (!this.searchQuery) return this.projects;

      const query = this.searchQuery.toLowerCase();
      return this.projects.filter(
        (project) =>
          project.name.toLowerCase().includes(query) ||
          project.id.toLowerCase().includes(query)
      );
    },

    projectsCount() {
      return this.filteredProjects.length;
    },
  },
  beforeRouteEnter(to, from, next) {
    if (!msalInstance.getAllAccounts().length) {
      next("/login");
    } else {
      next();
    }
  },
  async created() {
    await this.loadProjects();
  },
  methods: {
    async loadProjects() {
      this.isLoading = true;
      this.error = null;

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length) {
        const accessTokenRequest = {
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        };

        try {
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest
          );
          this.projects = await fetchProjects(response.accessToken);
        } catch (error) {
          console.error("Error fetching projects:", error);
          this.error =
            "Failed to load projects. Please try refreshing the page.";
        } finally {
          this.isLoading = false;
        }
      } else {
        this.isLoading = false;
        this.error = "No authenticated account found.";
      }
    },

    async login() {
      try {
        const currentAccount = msalInstance.getActiveAccount();
        await msalInstance.handleRedirectPromise();
        if (!currentAccount) {
          await msalInstance.loginPopup({
            scopes: [
              "openid",
              "profile",
              "email",
              "offline_access",
              "User.Read",
              "499b84ac-1321-427f-aa17-267ca6975798/user_impersonation",
            ],
          });
        }
        this.$router.push("/azure-ado-projects");
      } catch (error) {
        console.error("Login error:", error);
        this.error = "Login failed. Please try again.";
      }
    },

    getProjectIcon(projectName) {
      // Generate icon based on project name
      const icons = ["🚀", "💼", "🎯", "🔧", "⚡", "🌟", "📊", "🛠️"];
      const index = projectName.charCodeAt(0) % icons.length;
      return icons[index];
    },

    async refreshProjects() {
      this.isRefreshing = true;
      this.searchQuery = "";
      await this.loadProjects();
      setTimeout(() => {
        this.isRefreshing = false;
      }, 500);
    },

    formatProjectId(id) {
      return id.substring(0, 8) + "...";
    },
  },
  watch: {
    searchQuery(newValue) {
      sessionStorage.setItem("projectsSearchQuery", newValue);
    },
  },
  mounted() {
    const savedSearch = sessionStorage.getItem("projectsSearchQuery");
    if (savedSearch) {
      this.searchQuery = savedSearch;
    }
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Page Container */
.projects-page {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  background-color: #f8fafc;
  min-height: 100vh;
  color: #1e293b;
}

/* Main Container - Full Width */
.projects-container {
  padding: 2rem;
}

/* Page Header */
.page-header {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 2.5rem;
  border-radius: 1rem;
  border: 1px solid #bfdbfe;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.quick-stat {
  text-align: center;
}

.quick-stat-value {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
}

.quick-stat-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

/* Controls Section */
.controls-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Search */
.search-wrapper {
  position: relative;
  flex: 1;
  max-width: 500px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 3rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #f8fafc;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: #ffffff;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #94a3b8;
}

.clear-search {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: #94a3b8;
  transition: all 0.2s;
}

.clear-search:hover {
  background: #f1f5f9;
  color: #475569;
}

.clear-search svg {
  width: 16px;
  height: 16px;
}

/* Refresh Button */
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.refresh-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}

.refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

/* Projects Grid - 3 columns default, responsive */
.projects-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

@media (max-width: 1200px) {
  .projects-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
}

/* Project Card */
.project-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.project-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transform: translateY(-100%);
  transition: transform 0.2s ease;
}

.project-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.project-card:hover::before {
  transform: translateY(0);
}

.project-card:hover .project-arrow {
  transform: translateX(4px);
  background: #3b82f6;
  color: white;
}

/* Project Card Header */
.project-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Project Icon */
.project-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

/* Project Arrow */
.project-arrow {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 0.5rem;
  color: #94a3b8;
  transition: all 0.2s ease;
}

.project-arrow svg {
  width: 16px;
  height: 16px;
}

/* Project Content */
.project-content {
  flex: 1;
  min-width: 0;
}

.project-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #64748b;
}

.meta-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  margin-top: 2rem;
}

.empty-icon {
  margin-bottom: 1rem;
  opacity: 0.3;
}

.empty-icon svg {
  width: 80px;
  height: 80px;
  color: #cbd5e1;
}

.empty-state h3 {
  font-size: 1.25rem;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.empty-state p {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #64748b;
  gap: 1rem;
}

.loader {
  border: 2px solid #f1f5f9;
  border-radius: 50%;
  border-top: 2px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 4rem 2rem;
  background: #ffffff;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  margin-top: 2rem;
}

.error-icon {
  margin-bottom: 1rem;
  color: #ef4444;
}

.error-icon svg {
  width: 60px;
  height: 60px;
}

.error-state h3 {
  font-size: 1.25rem;
  color: #dc2626;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.error-state p {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Buttons */
.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.375rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
  margin: 0 0.25rem;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .projects-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
  }

  .controls-section {
    flex-direction: column;
  }

  .search-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .project-card {
    padding: 1.25rem;
  }
}

/* Hover Effects for Desktop Only */
@media (hover: hover) {
  .project-card:hover .project-icon {
    background: #e0f2fe;
    border-color: #3b82f6;
  }
}
</style>
